// Geocoding client for the verblijf-wizard maps (found-location / nature-release). Calls the api/ proxy
// GET /api/geocode/search + /api/geocode/reverse — NOT Nominatim directly — which is the faithful port of
// the Jmix fragments (they geocoded server-side with an identifying User-Agent). Routing through the api
// keeps us compliant with Nominatim's usage policy and centralizes the call origin. The access token is
// bearer-only (in memory), so these go through an authenticated fetch like the other src/api clients.
import { getAccessToken } from "../auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** A forward-geocoded coordinate. */
export interface GeoCoordinate {
  lat: number;
  lon: number;
}

/** The address fields resolved from a reverse-geocoded coordinate; any field may be null. */
export interface ReverseAddress {
  city: string | null;
  /** Municipality name — the SPA matches it to a gemeente option to set the gemeente select. */
  municipality: string | null;
  postalCode: string | null;
  street: string | null;
  houseNumber: string | null;
  houseNumberAddition: string | null;
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * Forward-geocode a free-text address query to its best coordinate. Returns null when the query is blank,
 * nothing matches (204), or the call fails — mirroring the Jmix "leave the pin as-is on failure" contract.
 */
export async function forwardGeocode(query: string): Promise<GeoCoordinate | null> {
  const trimmed = query.trim();
  if (!trimmed) return null;
  try {
    const res = await fetch(`${API_URL}/geocode/search?q=${encodeURIComponent(trimmed)}`, {
      headers: authHeaders(),
    });
    if (res.status === 204 || !res.ok) return null;
    const body = (await res.json()) as GeoCoordinate;
    if (typeof body?.lat !== "number" || typeof body?.lon !== "number") return null;
    return body;
  } catch {
    return null;
  }
}

/**
 * Reverse-geocode a coordinate to address fields. Returns null when nothing resolves (204) or the call
 * fails — so a map click keeps its pin even when the address can't be looked up (Jmix parity).
 */
export async function reverseGeocode(lat: number, lng: number): Promise<ReverseAddress | null> {
  try {
    const res = await fetch(
      `${API_URL}/geocode/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}`,
      { headers: authHeaders() },
    );
    if (res.status === 204 || !res.ok) return null;
    return (await res.json()) as ReverseAddress;
  } catch {
    return null;
  }
}

/** The address parts a forward-geocode query is built from. */
export interface AddressParts {
  street: string;
  houseNumber: string;
  houseNumberAddition: string;
  postalCode: string;
  city: string;
  /** Resolved gemeente (municipality) label, or "". */
  municipality: string;
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** Normalize a postal code the way Jmix does: strip whitespace, upper-case. */
export function normalizePostalCode(value: string): string | null {
  const normalized = blankToNull(value);
  return normalized === null ? null : normalized.replace(/\s+/g, "").toUpperCase();
}

/** Combine house number + addition (addition upper-cased, no separator) — Jmix combineHouseNumber. */
function combineHouseNumber(houseNumber: string, addition: string): string | null {
  const number = blankToNull(houseNumber);
  const add = blankToNull(addition);
  const normalizedAdd = add === null ? null : add.toUpperCase();
  if (number === null) return normalizedAdd;
  if (normalizedAdd === null) return number;
  return number + normalizedAdd;
}

/**
 * Build the forward-geocode query from address parts, mirroring Jmix's joinAddressParts ∘ buildSearchQuery:
 * street, combined house number, normalized postal code, city, municipality — blanks skipped, joined by a
 * single space. Returns "" when every part is blank (no query). Also used as the dedup/suppression key.
 */
export function buildAddressQuery(parts: AddressParts): string {
  return [
    blankToNull(parts.street),
    combineHouseNumber(parts.houseNumber, parts.houseNumberAddition),
    normalizePostalCode(parts.postalCode),
    blankToNull(parts.city),
    blankToNull(parts.municipality),
  ]
    .filter((p): p is string => p !== null)
    .join(" ");
}
